import { UseFormRegisterReturn } from 'react-hook-form';
import { useCallback } from 'react';

/**
 * @param registerReturn - The return value from react-hook-form's register()
 * @param setter - The store setter function
 * @param transform - Optional transform function for the value -> change the value to the type you want to store
 *
 * @example Basic usage
 * ```tsx
 * const setProductTitle = usePageDataStore(state => state.setProductTitle);
 * <input {...useFormStoreSync(register('title'), setProductTitle)} />
 * ```
 *
 * @example For numbers
 * ```tsx
 * const setPrice = usePageDataStore(state => state.updateFields);
 * <input
 *   type="number"
 *   {...useFormStoreSync(
 *     register('price', { valueAsNumber: true }),
 *     (v) => setPrice({ priceAmount: Number(v) || 0 })
 *   )}
 * />
 * ```
 */
export function useFormStoreSync<T = string>(
    registerReturn: UseFormRegisterReturn,
    setter: (value: T) => void,
    transform?: (value: unknown) => T
) {
    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            // Call the original react-hook-form onChange first
            registerReturn.onChange(event);

            // Update store immediately
            const rawValue = event.target.value;
            const finalValue = transform ? transform(rawValue) : (rawValue as T);
            setter(finalValue);
        },
        [registerReturn, setter, transform]
    );

    return {
        ...registerReturn,
        onChange: handleChange,
    };
}

/**
 * Convenience hook for number inputs with automatic transformation
 *
 * @example
 * ```tsx
 * <input type="number" {...useNumberSync(register('price'), (v) => setPrice({ priceAmount: v }))} />
 * ```
 */
export function useNumberSync(registerReturn: UseFormRegisterReturn, setter: (value: number) => void) {
    return useFormStoreSync(registerReturn, setter, v => {
        const num = Number(v);
        return isNaN(num) ? 0 : num;
    });
}
